import { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Mail, MapPin, ChevronLeft } from 'lucide-react';
import { FaInstagram, FaXTwitter, FaWhatsapp, FaLinkedin } from 'react-icons/fa6';
import { COMPANY_INFO, ROUTE_PATHS } from '@/lib/index';

const NAV_LINKS = [
  { label: 'الرئيسية', to: ROUTE_PATHS.HOME },
  { label: 'المقاولون والموردون', to: ROUTE_PATHS.CONTRACTORS },
  { label: 'الوظائف', to: ROUTE_PATHS.CAREERS },
  { label: 'فرص التمويل', to: ROUTE_PATHS.INVESTMENT },
];

export function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => { setIsOpen(false); }, [location]);

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setIsOpen(false);
  };

  return (
    <nav className={`fixed top-0 right-0 left-0 z-50 transition-all duration-500 ${
      scrolled ? 'bg-white/98 shadow-lg shadow-navy/8 border-b border-border' : 'bg-white/90 backdrop-blur-md border-b border-border/50'
    }`}>
      {/* Top accent bar */}
      <div className="h-1 bg-gradient-to-l from-navy via-gold to-navy" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-18 py-3">
          {/* Logo */}
          <NavLink to={ROUTE_PATHS.HOME} className="flex items-center gap-3 group">
            <img src="/images/logo_ibs_enhanced.png" alt="IBS" className="h-13 w-13 object-contain transition-transform duration-300 group-hover:scale-105" />
            <div className="hidden sm:block">
              <div className="text-navy font-black text-base leading-tight" style={{ fontFamily: 'Tajawal,sans-serif' }}>
                أنظمة المباني المبتكرة
              </div>
              <div className="text-gold text-xs font-semibold tracking-wide">Innovative Building Systems</div>
            </div>
          </NavLink>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                className={({ isActive }) =>
                  `px-4 py-2 text-sm rounded-lg font-medium transition-all duration-200 ${
                    isActive
                      ? 'bg-navy text-white'
                      : 'text-foreground hover:text-navy hover:bg-navy/8'
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>

          {/* CTA + Social */}
          <div className="hidden lg:flex items-center gap-3">
            <div className="flex items-center gap-2">
              <a href={COMPANY_INFO.social.instagram} target="_blank" rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-gold hover:bg-gold/10 transition-all duration-200">
                <FaInstagram size={16} />
              </a>
              <a href={COMPANY_INFO.social.twitter} target="_blank" rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-navy hover:bg-navy/10 transition-all duration-200">
                <FaXTwitter size={15} />
              </a>
              <a href={COMPANY_INFO.social.whatsapp} target="_blank" rel="noopener noreferrer"
                className="w-8 h-8 flex items-center justify-center rounded-full text-muted-foreground hover:text-green-600 hover:bg-green-50 transition-all duration-200">
                <FaWhatsapp size={16} />
              </a>
            </div>
            <button onClick={() => scrollTo('contact')}
              className="px-5 py-2.5 bg-navy text-white rounded-lg text-sm font-bold hover:bg-navy-light transition-all duration-200 hover:shadow-lg">
              تواصل معنا
            </button>
          </div>

          <button onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden p-2 rounded-lg text-navy hover:bg-navy/8 transition-colors">
            {isOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-white border-t border-border shadow-xl"
          >
            <div className="px-4 py-4 space-y-1">
              {NAV_LINKS.map((l) => (
                <NavLink key={l.to} to={l.to}
                  className={({ isActive }) =>
                    `block px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? 'bg-navy text-white' : 'text-foreground hover:bg-navy/8 hover:text-navy'}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="flex items-center gap-4 pt-3 px-4">
                <a href={COMPANY_INFO.social.instagram} target="_blank" rel="noopener noreferrer"><FaInstagram size={18} className="text-gold" /></a>
                <a href={COMPANY_INFO.social.twitter} target="_blank" rel="noopener noreferrer"><FaXTwitter size={17} className="text-navy" /></a>
                <a href={COMPANY_INFO.social.whatsapp} target="_blank" rel="noopener noreferrer"><FaWhatsapp size={18} className="text-green-600" /></a>
                <a href={COMPANY_INFO.social.linkedin} target="_blank" rel="noopener noreferrer"><FaLinkedin size={18} className="text-navy" /></a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}

export function Footer() {
  const scrollTo = (id: string) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <footer className="bg-navy text-white">
      {/* Gold top line */}
      <div className="h-1 bg-gradient-to-l from-gold via-gold-light to-gold" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="grid md:grid-cols-4 gap-10 mb-10">
          {/* Brand */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <img src="/images/logo_ibs_enhanced.png" alt="IBS" className="h-16 w-16 object-contain rounded-xl bg-white/10 p-1" />
            </div>
            <div className="text-white font-black text-lg mb-1" style={{ fontFamily: 'Tajawal,sans-serif' }}>
              أنظمة المباني المبتكرة
            </div>
            <div className="text-gold text-xs mb-3 font-semibold tracking-wide">Innovative Building Systems</div>
            <p className="text-white/70 text-sm leading-relaxed">
              متخصصون في بناء الفنادق الفاخرة في المنطقة المركزية بالمدينة المنورة.
            </p>
            <p className="text-gold text-sm mt-3 font-medium" style={{ fontFamily: 'Amiri,serif' }}>
              "نبني الفخامة... نسلّم الإتقان"
            </p>
            {/* Social */}
            <div className="flex items-center gap-3 mt-5">
              <a href={COMPANY_INFO.social.instagram} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-gold/30 transition-colors">
                <FaInstagram size={16} />
              </a>
              <a href={COMPANY_INFO.social.twitter} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-gold/30 transition-colors">
                <FaXTwitter size={15} />
              </a>
              <a href={COMPANY_INFO.social.whatsapp} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-green-500/40 transition-colors">
                <FaWhatsapp size={16} />
              </a>
              <a href={COMPANY_INFO.social.linkedin} target="_blank" rel="noopener noreferrer"
                className="w-9 h-9 bg-white/10 rounded-full flex items-center justify-center hover:bg-gold/30 transition-colors">
                <FaLinkedin size={16} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-gold font-bold mb-4 text-sm uppercase tracking-wider">روابط سريعة</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'الرئيسية', action: () => scrollTo('hero') },
                { label: 'من نحن', action: () => scrollTo('about') },
                { label: 'خدماتنا', action: () => scrollTo('services') },
                { label: 'مشاريعنا', action: () => scrollTo('projects') },
                { label: 'تواصل معنا', action: () => scrollTo('contact') },
              ].map((l) => (
                <li key={l.label}>
                  <button onClick={l.action}
                    className="text-white/70 text-sm hover:text-gold transition-colors flex items-center gap-1.5">
                    <ChevronLeft size={12} className="text-gold/60" />
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Pages */}
          <div>
            <h4 className="text-gold font-bold mb-4 text-sm uppercase tracking-wider">صفحاتنا</h4>
            <ul className="space-y-2.5">
              {[
                { label: 'المقاولون والموردون', to: ROUTE_PATHS.CONTRACTORS },
                { label: 'الوظائف والتوظيف', to: ROUTE_PATHS.CAREERS },
                { label: 'فرص التمويل', to: ROUTE_PATHS.INVESTMENT },
              ].map((l) => (
                <li key={l.to}>
                  <NavLink to={l.to}
                    className="text-white/70 text-sm hover:text-gold transition-colors flex items-center gap-1.5">
                    <ChevronLeft size={12} className="text-gold/60" />
                    {l.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-gold font-bold mb-4 text-sm uppercase tracking-wider">تواصل معنا</h4>
            <div className="space-y-3">
              <a href={`tel:${COMPANY_INFO.phone}`} className="flex items-start gap-2 text-white/70 text-sm hover:text-gold transition-colors">
                <Phone size={14} className="text-gold mt-0.5 flex-shrink-0" />
                <span dir="ltr">{COMPANY_INFO.phone}</span>
              </a>
              <a href={`mailto:${COMPANY_INFO.email}`} className="flex items-start gap-2 text-white/70 text-sm hover:text-gold transition-colors">
                <Mail size={14} className="text-gold mt-0.5 flex-shrink-0" />
                <span dir="ltr">{COMPANY_INFO.email}</span>
              </a>
              <div className="flex items-start gap-2">
                <MapPin size={14} className="text-gold mt-0.5 flex-shrink-0" />
                <span className="text-white/70 text-sm">{COMPANY_INFO.location}</span>
              </div>
            </div>
            {/* Parent Company */}
            <div className="mt-5 p-3 bg-white/8 rounded-xl border border-white/10">
              <div className="text-white/50 text-xs mb-1">الشركة الأم</div>
              <a href="http://one-investment.com" target="_blank" rel="noopener noreferrer"
                className="text-gold font-semibold text-sm hover:text-gold-light transition-colors">
                شركة ون للاستثمار
              </a>
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-white/50 text-xs">
            © 2025 شركة أنظمة المباني المبتكرة للمقاولات. جميع الحقوق محفوظة.
          </p>
          <div className="flex items-center gap-2 text-xs text-white/50">
            <span>تحت إشراف:</span>
            <span className="text-gold font-medium">الشيخ أسامة المسعود</span>
            <span>&</span>
            <span className="text-gold font-medium">شركة ون للاستثمار</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
